import React, { useState, useEffect, useRef } from "react";
import {
  Settings,
  Play,
  CheckCircle2,
  XCircle,
  Coffee,
  Plus,
  Trash2,
  Edit3,
  Heart,
  Star,
  Sparkles,
  Wand2,
  MessageCircle,
  User,
  Bot,
  Lock,
  ArrowRight,
} from "lucide-react";

const DEFAULT_TASKS = [
  "做数学题",
  "临摹字帖",
  "学习古诗",
  "画画",
  "看英文动画",
  "玩耍实践",
  "整理家务",
  "跳绳100个",
  "学习语文",
  "学习英语",
];

const API_KEY = "96658051bf954c1ca80d9686d6c5b39d.JqLCJcCmK9kq5GGq";

const COLORS = [
  "#FFB3BA",
  "#FFDFBA",
  "#FFFFBA",
  "#BAFFC9",
  "#BAE1FF",
  "#E8BAFF",
  "#FFBAE8",
  "#FFD8B1",
  "#C1F0F6",
];

// === 本地缓存读取工具 ===
const loadSaved = (key: string, defaultValue: any) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error("读取缓存失败", e);
  }
  return defaultValue;
};

export default function App() {
  // === 登录拦截状态 ===
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() =>
    loadSaved("app_isLoggedIn", false)
  );
  const [inviteCode, setInviteCode] = useState<string>("");
  const [loginError, setLoginError] = useState<string>("");

  // === 个性化设定状态 ===
  const [ageMode, setAgeMode] = useState<"child" | "youth">(() =>
    loadSaved("app_ageMode", "child")
  );
  const [userName, setUserName] = useState<string>(() =>
    loadSaved("app_userName", "安安")
  );
  const [aiSpecies, setAiSpecies] = useState<string>(() =>
    loadSaved("app_aiSpecies", "小猫")
  );
  const [aiName, setAiName] = useState<string>(() =>
    loadSaved("app_aiName", "哈基米")
  );

  // === 动态模型与账单状态 ===
  const [chatModel, setChatModel] = useState<string>(() =>
    loadSaved("app_chatModel", "glm-4.5-air")
  );
  const [billing, setBilling] = useState<any>(() =>
    loadSaved("app_billing", { totalCost: 0, textTokens: 0, ttsChars: 0 })
  );

  const [tasks, setTasks] = useState<any[]>(() =>
    loadSaved("app_tasks", DEFAULT_TASKS)
  );
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [todayTasks, setTodayTasks] = useState<any[]>(() =>
    loadSaved("app_todayTasks", [])
  );
  const [rotations, setRotations] = useState<number[]>([0, 120, 240]);
  const [stats, setStats] = useState<any>(() =>
    loadSaved("app_stats", { done: 0, failed: 0, leave: 0, leaveCards: 3 })
  );
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [newTaskInput, setNewTaskInput] = useState<string>("");

  const [aiPraise, setAiPraise] = useState<string>("");
  const [isGeneratingPraise, setIsGeneratingPraise] = useState<boolean>(false);
  const [aiInspirationTopic, setAiInspirationTopic] = useState<string>("");
  const [isGeneratingTasks, setIsGeneratingTasks] = useState<boolean>(false);

  const [showChat, setShowChat] = useState<boolean>(false);
  const [voiceRatio, setVoiceRatio] = useState<number>(50);

  const [messages, setMessages] = useState<any[]>(() => {
    const initialMessages = loadSaved("app_messages", null);
    if (initialMessages && initialMessages.length > 0) {
      return initialMessages.map((m: any) =>
        m.type === "audio" ? { ...m, type: "text", audioUrl: null } : m
      );
    }
    return [
      {
        role: "ai",
        text: `✨ 安安你好呀！我是你的好朋友-哈基米。今天想和我聊点什么~`,
        type: "text",
      },
    ];
  });

  const [inputText, setInputText] = useState<string>("");
  const [isChatting, setIsChatting] = useState<boolean>(false);

  const [playingMsgIndex, setPlayingMsgIndex] = useState<any>(null);
  const audioRef = useRef<any>(null);
  const chatContainerRef = useRef<any>(null);

  // === 气泡拖拽状态 (含防抖容差) ===
  const [bubblePos, setBubblePos] = useState({ x: 0, y: 0 });
  const [isDraggingBubble, setIsDraggingBubble] = useState<boolean>(false);
  const bubbleDragStart = useRef({ x: 0, y: 0 });
  const hasMovedBubble = useRef<boolean>(false);
  const clickStartPos = useRef({ x: 0, y: 0 });

  const onBubblePointerDown = (e: any) => {
    setIsDraggingBubble(true);
    hasMovedBubble.current = false;
    clickStartPos.current = { x: e.clientX, y: e.clientY };
    bubbleDragStart.current = {
      x: e.clientX - bubblePos.x,
      y: e.clientY - bubblePos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onBubblePointerMove = (e: any) => {
    if (!isDraggingBubble) return;

    if (!hasMovedBubble.current) {
      const dx = e.clientX - clickStartPos.current.x;
      const dy = e.clientY - clickStartPos.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > 8) {
        hasMovedBubble.current = true;
      }
    }

    if (hasMovedBubble.current) {
      setBubblePos({
        x: e.clientX - bubbleDragStart.current.x,
        y: e.clientY - bubbleDragStart.current.y,
      });
    }
  };

  const onBubblePointerUp = (e: any) => {
    setIsDraggingBubble(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (!hasMovedBubble.current) {
      setShowChat(true);
    }
  };

  // === 持久化与监听 ===
  useEffect(() => {
    localStorage.setItem("app_isLoggedIn", JSON.stringify(isLoggedIn));
  }, [isLoggedIn]);
  useEffect(() => {
    localStorage.setItem("app_ageMode", JSON.stringify(ageMode));
  }, [ageMode]);
  useEffect(() => {
    localStorage.setItem("app_userName", JSON.stringify(userName));
  }, [userName]);
  useEffect(() => {
    localStorage.setItem("app_aiSpecies", JSON.stringify(aiSpecies));
  }, [aiSpecies]);
  useEffect(() => {
    localStorage.setItem("app_aiName", JSON.stringify(aiName));
  }, [aiName]);
  useEffect(() => {
    localStorage.setItem("app_tasks", JSON.stringify(tasks));
  }, [tasks]);
  useEffect(() => {
    localStorage.setItem("app_todayTasks", JSON.stringify(todayTasks));
  }, [todayTasks]);
  useEffect(() => {
    localStorage.setItem("app_stats", JSON.stringify(stats));
  }, [stats]);
  useEffect(() => {
    localStorage.setItem("app_messages", JSON.stringify(messages));
  }, [messages]);
  useEffect(() => {
    localStorage.setItem("app_chatModel", JSON.stringify(chatModel));
  }, [chatModel]);
  useEffect(() => {
    localStorage.setItem("app_billing", JSON.stringify(billing));
  }, [billing]);

  useEffect(() => {
    setMessages((prev: any[]) => {
      if (prev.length === 0) return prev;
      const newMsgs = [...prev];
      if (
        newMsgs[0].role === "ai" &&
        newMsgs[0].text.includes("今天想和我聊点什么")
      ) {
        const updatedWelcomeText = `✨ ${userName}你好呀！我是你的好朋友-${aiName}。今天想和我聊点什么~`;
        if (newMsgs[0].text !== updatedWelcomeText) {
          newMsgs[0] = { ...newMsgs[0], text: updatedWelcomeText };
          return newMsgs;
        }
      }
      return prev;
    });
  }, [userName, aiName]);

  const handleLogin = () => {
    if (inviteCode === "111314") {
      setIsLoggedIn(true);
    } else {
      setLoginError("邀请码不对哦，再试一次吧！");
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isChatting]);

  useEffect(() => {
    setRotations([
      Math.floor(Math.random() * 360),
      Math.floor(Math.random() * 360),
      Math.floor(Math.random() * 360),
    ]);
  }, []);

  // === 计费核算核心函数 ===
  const updateTextBilling = (usage: any, modelUsed: string) => {
    if (!usage) return;
    let cost = 0;
    const inTokens = usage.prompt_tokens || 0;
    const outTokens = usage.completion_tokens || 0;

    if (modelUsed === "glm-4.5-air") {
      const inCost = inTokens * 0.0000008;
      const outCost =
        outTokens <= 200 ? outTokens * 0.000002 : outTokens * 0.000006;
      cost = inCost + outCost;
    } else if (modelUsed === "glm-5.3-flash") {
      // 按照 GLM-5.3-Flash 价格计算：输入0.4元/百万，输出1.4元/百万
      const inCost = inTokens * 0.0000004;
      const outCost = outTokens * 0.0000014;
      cost = inCost + outCost;
    }
    // glm-4.7-flash 费用为 0，无需增加 cost

    setBilling((b: any) => ({
      ...b,
      totalCost: b.totalCost + cost,
      textTokens: b.textTokens + (usage.total_tokens || 0),
    }));
  };

  const updateTTSBilling = (textLen: number) => {
    const cost = textLen * 0.0002;
    setBilling((b: any) => ({
      ...b,
      totalCost: b.totalCost + cost,
      ttsChars: b.ttsChars + textLen,
    }));
  };

  const callGemini = async (prompt: any) => {
    if (!API_KEY) {
      alert("请先在代码中填入您的 API Key 哦！");
      return null;
    }
    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: chatModel,
            messages: [{ role: "user", content: prompt }],
          }),
        }
      );
      const data = await response.json();
      if (data.error) {
        alert("智谱官方报错啦：" + data.error.message);
        return null;
      }

      // 更新账单
      updateTextBilling(data.usage, chatModel);

      return data.choices[0].message.content;
    } catch (error: any) {
      console.error("AI 请求失败:", error);
      alert("网络连接失败！具体原因：" + error.message);
      return null;
    }
  };

  const fetchTTS = async (text: string) => {
    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/audio/speech",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: "glm-tts",
            input: text,
            voice: "jam",
            response_format: "wav",
            speed: 1.2,
          }),
        }
      );
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) return null;
      if (!response.ok) throw new Error("TTS Request failed");

      const blob = await response.blob();
      return URL.createObjectURL(new Blob([blob], { type: "audio/wav" }));
    } catch (e: any) {
      console.error("语音合成失败", e);
      return null;
    }
  };

  const playAudio = (url: any, index: any) => {
    if (audioRef.current) audioRef.current.pause();
    if (playingMsgIndex === index) {
      setPlayingMsgIndex(null);
      return;
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    setPlayingMsgIndex(index);
    audio.play();
    audio.onended = () => {
      setPlayingMsgIndex(null);
    };
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isChatting) return;

    const userMsg = inputText.trim();
    const newMessages = [
      ...messages,
      { role: "user", text: userMsg, type: "text" },
    ];
    setMessages(newMessages);
    setInputText("");
    setIsChatting(true);

    const ageInstruction =
      ageMode === "child"
        ? `【儿童模式】如果${userName}问你知识或百科问题，你必须用小孩子能听懂的童话比喻来解释复杂的知识，将抽象概念具象化。`
        : `【青年模式】如果${userName}问你知识或百科问题，请用正常的、专业的、准确且严谨的语言进行全面解答。`;

    const systemPrompt = `你是一个全知全能（精通天文地理、语数外等）但性格极其温和、极具耐心的魔法${aiSpecies}（名字叫：${aiName}），是${userName}的专属陪聊宠物。

    ${ageInstruction}

    【性格与回复要求】
    1. 语气：极其软萌、傲娇又粘人，一般自称“本${aiName}”。绝不居高临下地说教。
    2. 字数控制：日常闲聊控制在 30~50 字。若被问及知识或${userName}有明确字数要求（如简单解释<100，详细解释<500），则严格遵循，做到不浪费、不啰嗦、不敷衍。

    【输出格式——必须严格遵守】
    不要输出任何解释或代码块标记，也不要输出JSON，直接回复你要说的话即可。
    在回复中尽情加入${aiSpecies}的拟声词，大量使用符合${aiSpecies}的小红书风格可爱 Emoji、颜文字，多用 "~" 和 "！" 等标点让排版极其生动活泼。`;

    const chatHistory = newMessages.map((m: any) => ({
      role: m.role === "ai" ? "assistant" : "user",
      content: m.text,
    }));

    try {
      const response = await fetch(
        "https://open.bigmodel.cn/api/paas/v4/chat/completions",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model: chatModel,
            messages: [
              { role: "system", content: systemPrompt },
              ...chatHistory,
            ],
          }),
        }
      );
      const data = await response.json();

      if (data.error) {
        setMessages((prev: any[]) => [
          ...prev,
          {
            role: "ai",
            text: `哎呀，魔法信号断了，${aiName}没听清~`,
            type: "text",
          },
        ]);
      } else if (data.choices && data.choices[0]) {
        updateTextBilling(data.usage, chatModel);

        const rawReply = data.choices[0].message.content.trim();
        const displayMessage = rawReply;
        const shouldUseVoice = Math.random() * 100 < voiceRatio;

        if (shouldUseVoice) {
          setMessages((prev: any[]) => [
            ...prev,
            { role: "ai", text: displayMessage, type: "loading_audio" },
          ]);

          const ttsText = rawReply
            .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, "")
            .replace(/[~～]/g, "，")
            .replace(/[！!]+/g, "。")
            .replace(/[\[\]【】\*#_]/g, "");

          updateTTSBilling(ttsText.length);
          const audioUrl = await fetchTTS(ttsText);

          setMessages((prev: any[]) => {
            const newMsgs = [...prev];
            if (audioUrl) {
              newMsgs[newMsgs.length - 1] = {
                role: "ai",
                text: displayMessage,
                type: "audio",
                audioUrl: audioUrl,
              };
            } else {
              newMsgs[newMsgs.length - 1] = {
                role: "ai",
                text: displayMessage,
                type: "text",
              };
            }
            return newMsgs;
          });
        } else {
          setMessages((prev: any[]) => [
            ...prev,
            { role: "ai", text: displayMessage, type: "text" },
          ]);
        }
      }
    } catch (e: any) {
      setMessages((prev: any[]) => [
        ...prev,
        {
          role: "ai",
          text: `网络好像打盹了，稍后再跟${aiName}说话哦~`,
          type: "text",
        },
      ]);
    }
    setIsChatting(false);
  };

  const generatePraise = async () => {
    setIsGeneratingPraise(true);
    const finishedTasks = todayTasks
      .filter((t: any) => t.status === "done")
      .map((t: any) => t.text)
      .join("、");
    const onLeaveTasks = todayTasks.filter(
      (t: any) => t.status === "leave"
    ).length;

    let prompt = `你现在是一个非常温柔、充满童趣的AI夸夸精灵。你正在鼓励${
      ageMode === "child" ? "一个6岁的小孩" : "一个年轻人"
    }，名字叫${userName}。
    请用不超过50个字，带上可爱的Emoji，给${userName}一段专属鼓励。
    语气要像童话里的精灵，不要像老师说教。`;

    if (finishedTasks)
      prompt += `\n${userName}今天完成了这些挑战：${finishedTasks}。请夸奖${userName}的努力。`;
    if (onLeaveTasks > 0)
      prompt += `\n${userName}今天还使用了请假卡休息。请温柔地告诉${userName}：合理休息也是一种很棒的能力，懂得照顾自己很棒。`;
    if (!finishedTasks && onLeaveTasks === 0)
      prompt += `\n${userName}今天遇到了点困难，没有完成任务。请温柔地抱抱${userName}，告诉${userName}没关系，明天会有新魔法。`;

    const result = await callGemini(prompt);
    if (result) setAiPraise(result);
    setIsGeneratingPraise(false);
  };

  const generateTaskInspiration = async () => {
    if (!aiInspirationTopic.trim()) return;
    setIsGeneratingTasks(true);
    const prompt = `我正在为一个${
      ageMode === "child" ? "6岁的孩子" : "年轻人"
    }(名字叫${userName})设计日常微小挑战任务。要求：极低门槛、有趣、游戏化。
    请根据这个主题：“${aiInspirationTopic}”，生成3个任务。每个任务不超过15个字。
    请严格只返回JSON数组格式，像这样：["任务1", "任务2", "任务3"]，不要有任何其他解释文字和Markdown标记。`;

    const result = await callGemini(prompt);
    if (result) {
      try {
        const cleaned = result
          .replace(/```json/g, "")
          .replace(/```/g, "")
          .trim();
        const newTasks = JSON.parse(cleaned);
        if (Array.isArray(newTasks)) {
          setTasks([...tasks, ...newTasks]);
          setAiInspirationTopic("");
          alert("✨ 灵感任务已成功加入扭蛋机！");
        }
      } catch (e: any) {
        alert("AI 生成的格式有些调皮，请重试一下。");
      }
    }
    setIsGeneratingTasks(false);
  };

  const spinWheel = () => {
    if (isSpinning || tasks.length === 0) return;
    setIsSpinning(true);
    setTodayTasks([]);
    setAiPraise("");

    const baseSpins = [360 * 5, 360 * 6, 360 * 7];
    const newRotations = rotations.map(
      (rot: any, i: any) => rot + baseSpins[i] + Math.floor(Math.random() * 360)
    );
    setRotations(newRotations);

    setTimeout(() => {
      calculateSelectedTasks(newRotations);
      setIsSpinning(false);
    }, 4000);
  };

  const calculateSelectedTasks = (currentRotations: any) => {
    const numTasks = tasks.length;
    const anglePerTask = 360 / numTasks;

    const selectedIndices = currentRotations.map((rot: any) => {
      let effectiveAngle = (rot % 360) + 90;
      let selectedAngle = (360 - effectiveAngle) % 360;
      if (selectedAngle < 0) selectedAngle += 360;
      return Math.floor(selectedAngle / anglePerTask) % numTasks;
    });

    const uniqueIndices = Array.from(new Set(selectedIndices));
    const newTodayTasks = uniqueIndices.map((index: any) => ({
      id: Date.now() + index,
      text: tasks[index],
      status: "pending",
    }));
    setTodayTasks(newTodayTasks);
  };

  const handleTaskAction = (taskId: any, action: any) => {
    setTodayTasks((prev: any[]) =>
      prev.map((t: any) => {
        if (t.id === taskId) {
          if (t.status !== "pending") return t;
          if (action === "done")
            setStats((s: any) => ({ ...s, done: s.done + 1 }));
          else if (action === "failed")
            setStats((s: any) => ({ ...s, failed: s.failed + 1 }));
          else if (action === "leave") {
            if (stats.leaveCards <= 0) {
              alert("本月的请假卡已经用完啦！必须勇敢面对挑战哦！");
              return t;
            }
            setStats((s: any) => ({
              ...s,
              leave: s.leave + 1,
              leaveCards: s.leaveCards - 1,
            }));
          }
          return { ...t, status: action };
        }
        return t;
      })
    );
  };

  const renderWheel = () => {
    const numTasks = Math.max(tasks.length, 1);
    const anglePerTask = 360 / numTasks;
    let currentAngle = 0;

    const paths = tasks.map((task: any, index: any) => {
      const startAngle = currentAngle;
      const endAngle = currentAngle + anglePerTask;
      currentAngle = endAngle;

      const x1 = 50 + 50 * Math.cos((Math.PI * startAngle) / 180);
      const y1 = 50 + 50 * Math.sin((Math.PI * startAngle) / 180);
      const x2 = 50 + 50 * Math.cos((Math.PI * endAngle) / 180);
      const y2 = 50 + 50 * Math.sin((Math.PI * endAngle) / 180);

      const largeArcFlag = anglePerTask > 180 ? 1 : 0;
      const pathData = `M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

      const textAngle = startAngle + anglePerTask / 2;
      const textRadius = 35;
      const textX = 50 + textRadius * Math.cos((Math.PI * textAngle) / 180);
      const textY = 50 + textRadius * Math.sin((Math.PI * textAngle) / 180);

      return (
        <g key={index}>
          <path
            d={pathData}
            fill={COLORS[index % COLORS.length]}
            stroke="white"
            strokeWidth="0.5"
          />
          <text
            x={textX}
            y={textY}
            fill="#4b5563"
            fontSize="3.5"
            fontWeight="bold"
            textAnchor="middle"
            transform={`rotate(${textAngle + 90} ${textX} ${textY})`}
            className="pointer-events-none select-none"
          >
            {task.length > 8 ? task.substring(0, 8) + "..." : task}
          </text>
        </g>
      );
    });

    return (
      <div className="relative w-full max-w-md aspect-square mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-stone-700 shadow-xl overflow-hidden bg-white">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            {paths}
          </svg>
        </div>
        <div className="absolute inset-0 pointer-events-none drop-shadow-lg">
          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[0]}deg)`,
              transition: "transform 3s cubic-bezier(0.2, 0.8, 0.3, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 48 50 L 50 15 L 52 50 Z" fill="#EF4444" />
            </svg>
          </div>
          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[1]}deg)`,
              transition: "transform 3.5s cubic-bezier(0.2, 0.8, 0.25, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 48.5 50 L 50 15 L 51.5 50 Z" fill="#3B82F6" />
            </svg>
          </div>
          <div
            className="absolute inset-0 origin-center"
            style={{
              transform: `rotate(${rotations[2]}deg)`,
              transition: "transform 4s cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <path d="M 49 50 L 50 15 L 51 50 Z" fill="#10B981" />
            </svg>
          </div>
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            <circle cx="50" cy="50" r="5" fill="#374151" />
            <circle cx="50" cy="50" r="2" fill="white" />
          </svg>
        </div>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4 font-sans relative overflow-hidden">
        <div className="absolute top-10 left-10 text-pink-200 animate-bounce">
          <Sparkles className="w-12 h-12" />
        </div>
        <div className="absolute bottom-20 right-10 text-yellow-200 animate-pulse">
          <Star className="w-16 h-16 fill-current" />
        </div>

        <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl p-8 border border-stone-100 z-10 relative">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-pink-100 p-4 rounded-full mb-4">
              <Lock className="w-10 h-10 text-pink-500" />
            </div>
            <h1 className="text-2xl font-black text-stone-700">
              魔法扭蛋机入口
            </h1>
            <p className="text-stone-400 text-sm mt-2">
              请输入邀请码和基础设定
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-bold text-stone-600 block mb-1">
                小主人的昵称
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  value={userName}
                  onChange={(e: any) => setUserName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-400 focus:bg-white transition-all"
                  placeholder="例如：安安"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-stone-600 block mb-1">
                  AI 名字
                </label>
                <div className="relative">
                  <Bot className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={aiName}
                    onChange={(e: any) => setAiName(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-stone-600 block mb-1">
                  AI 物种
                </label>
                <div className="relative">
                  <Wand2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input
                    type="text"
                    value={aiSpecies}
                    onChange={(e: any) => setAiSpecies(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition-all"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-stone-600 block mb-1">
                邀请码 (六位数)
              </label>
              <input
                type="password"
                maxLength={6}
                value={inviteCode}
                onChange={(e: any) => {
                  setInviteCode(e.target.value.slice(0, 6));
                  setLoginError("");
                }}
                onKeyDown={(e: any) => e.key === "Enter" && handleLogin()}
                className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white transition-all text-center tracking-widest font-mono text-lg"
                placeholder="••••••"
              />
              {loginError && (
                <p className="text-red-500 text-xs mt-2 text-center animate-bounce">
                  {loginError}
                </p>
              )}
            </div>

            <button
              onClick={handleLogin}
              className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 rounded-xl shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98] flex justify-center items-center gap-2"
            >
              开启魔法大门 <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 font-sans p-4 md:p-8 flex flex-col relative pb-20 md:pb-8">
      <style>
        {`
          @keyframes blob-shape {
            0%, 100% { border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%; }
            33% { border-radius: 55% 45% 45% 55% / 50% 55% 45% 50%; }
            66% { border-radius: 45% 55% 55% 45% / 55% 45% 50% 50%; }
          }
          @keyframes jelly-bounce {
            0%, 100% { transform: scale(1, 1) translateY(0); }
            5%  { transform: scale(1.15, 0.85) translateY(0); }
            15% { transform: scale(0.85, 1.15) translateY(-25px); }
            25% { transform: scale(1.05, 0.95) translateY(0); }
            32% { transform: scale(0.95, 1.05) translateY(-8px); }
            40% { transform: scale(1.02, 0.98) translateY(0); }
            50% { transform: scale(1, 1) translateY(0); }
          }
          @keyframes eye-blink {
            0%, 92%, 100% { transform: scaleY(1); }
            96% { transform: scaleY(0.1); }
          }
        `}
      </style>

      <header className="flex justify-between items-center mb-6 bg-white p-4 rounded-2xl shadow-sm border border-stone-100">
        <div className="flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-yellow-500" />
          <h1 className="text-xl md:text-3xl font-black text-stone-700">
            {userName}的超级通关扭蛋机
          </h1>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 rounded-full hover:bg-stone-100 text-stone-500 transition"
        >
          <Settings className="w-6 h-6" />
        </button>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 flex-1">
        <div className="lg:w-1/2 flex flex-col items-center justify-center bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-stone-100">
          {renderWheel()}
          <button
            onClick={spinWheel}
            disabled={isSpinning || tasks.length === 0}
            className={`mt-10 px-8 py-4 rounded-full text-xl font-bold text-white shadow-lg transition-all flex items-center gap-2
              ${
                isSpinning
                  ? "bg-stone-300 cursor-not-allowed scale-95"
                  : "bg-gradient-to-r from-orange-400 to-pink-500 hover:scale-105 active:scale-95"
              }`}
          >
            <Play className="w-6 h-6 fill-current" />
            {isSpinning ? "命运指针转动中..." : "启动！抽取3个挑战！"}
          </button>
        </div>

        <div className="lg:w-1/2 flex flex-col gap-6">
          <div className="bg-white rounded-3xl p-5 md:p-6 shadow-sm border border-stone-100 flex-1">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400 fill-current" />{" "}
              今日掉落的挑战
            </h2>

            {todayTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-stone-400 text-center">
                <p>今天还没有抽卡哦！</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {todayTasks.map((task: any) => (
                  <div
                    key={task.id}
                    className="flex flex-col xl:flex-row justify-between items-start xl:items-center p-3 md:p-4 bg-stone-50 rounded-2xl border border-stone-200 gap-3"
                  >
                    <span
                      className={`text-base md:text-lg font-medium ${
                        task.status !== "pending"
                          ? "line-through text-stone-400"
                          : ""
                      }`}
                    >
                      {task.text}
                    </span>

                    {task.status === "pending" ? (
                      <div className="flex flex-wrap gap-2 w-full xl:w-auto justify-end">
                        <button
                          onClick={() => handleTaskAction(task.id, "done")}
                          className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition text-sm font-bold flex-1 xl:flex-none justify-center"
                        >
                          <CheckCircle2 className="w-4 h-4" /> 搞定
                        </button>
                        <button
                          onClick={() => handleTaskAction(task.id, "failed")}
                          className="flex items-center gap-1 px-3 py-2 bg-stone-200 text-stone-600 rounded-lg hover:bg-stone-300 transition text-sm font-bold flex-1 xl:flex-none justify-center"
                        >
                          <XCircle className="w-4 h-4" /> 没完成
                        </button>
                        <button
                          onClick={() => handleTaskAction(task.id, "leave")}
                          className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-bold w-full xl:w-auto justify-center mt-2 xl:mt-0"
                        >
                          <Coffee className="w-4 h-4" /> 请假卡
                        </button>
                      </div>
                    ) : (
                      <span
                        className={`font-bold px-4 py-1 rounded-full text-sm self-end xl:self-auto
                        ${
                          task.status === "done"
                            ? "bg-green-100 text-green-600"
                            : task.status === "failed"
                            ? "bg-stone-200 text-stone-500"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        {task.status === "done"
                          ? "✅ 通关"
                          : task.status === "failed"
                          ? "❌ 未完成"
                          : "🛌 休息中"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {todayTasks.length > 0 &&
              todayTasks.every((t: any) => t.status !== "pending") && (
                <div className="mt-6 p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-purple-700 flex items-center gap-1">
                      <Wand2 className="w-5 h-5" /> 夸夸精灵的信
                    </h3>
                    <button
                      onClick={generatePraise}
                      disabled={isGeneratingPraise}
                      className="px-3 py-1.5 bg-purple-200 text-purple-700 rounded-lg text-sm font-bold hover:bg-purple-300 transition disabled:opacity-50"
                    >
                      {isGeneratingPraise
                        ? "精灵思考中..."
                        : aiPraise
                        ? "重新呼唤精灵"
                        : "召唤夸夸精灵"}
                    </button>
                  </div>
                  {aiPraise ? (
                    <p className="text-purple-800 font-medium leading-relaxed bg-white/60 p-3 rounded-xl">
                      {aiPraise}
                    </p>
                  ) : (
                    <p className="text-purple-400 text-sm">
                      点击召唤，听听精灵想对{userName}说什么~
                    </p>
                  )}
                </div>
              )}
          </div>

          <div className="bg-white rounded-3xl p-5 md:p-6 shadow-sm border border-stone-100">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-6 h-6 text-pink-400 fill-current" /> 当月战绩
            </h2>
            <div className="grid grid-cols-4 gap-2 mb-6 text-center">
              <div className="bg-green-50 p-2 rounded-xl border border-green-100">
                <div className="text-xl font-black text-green-600">
                  {stats.done}
                </div>
                <div className="text-xs text-green-700">通关</div>
              </div>
              <div className="bg-stone-50 p-2 rounded-xl border border-stone-200">
                <div className="text-xl font-black text-stone-600">
                  {stats.failed}
                </div>
                <div className="text-xs text-stone-500">未完成</div>
              </div>
              <div className="bg-blue-50 p-2 rounded-xl border border-blue-100">
                <div className="text-xl font-black text-blue-600">
                  {stats.leave}
                </div>
                <div className="text-xs text-blue-700">请假</div>
              </div>
              <div className="bg-pink-50 p-2 rounded-xl border border-pink-100">
                <div className="text-xl font-black text-pink-500">
                  {stats.leaveCards}
                </div>
                <div className="text-xs text-pink-600">剩余请假卡</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSettings && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-stone-50">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Settings className="w-5 h-5" /> 后台管理
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-stone-400 hover:text-stone-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-6">
              {/* === API 模型选择与账单看板 === */}
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-slate-700 flex items-center gap-1">
                    <Coffee className="w-4 h-4" /> API 消耗与账单
                  </h3>
                  <button
                    onClick={() =>
                      setBilling({ totalCost: 0, textTokens: 0, ttsChars: 0 })
                    }
                    className="text-xs text-red-500 hover:underline hover:text-red-600 font-medium"
                  >
                    重置账单
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center mb-4">
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                    <div className="text-base font-black text-slate-800">
                      ¥ {billing.totalCost.toFixed(4)}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      累计产生费用
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                    <div className="text-base font-black text-blue-600">
                      {billing.textTokens}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      消耗 Tokens
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                    <div className="text-base font-black text-green-600">
                      {billing.ttsChars}
                    </div>
                    <div className="text-[10px] text-slate-500">语音字数</div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200">
                  <label className="text-xs text-slate-600 block mb-2 font-medium">
                    聊天使用的 AI 核心引擎
                  </label>
                  <div className="flex flex-col gap-2">
                    {/* 1. 将 GLM-5.3-Flash 移到这里 */}
                    <label className="flex items-center gap-2 text-sm cursor-pointer text-stone-700">
                      <input
                        type="radio"
                        name="chatModel"
                        checked={chatModel === "glm-5.3-flash"}
                        onChange={() => setChatModel("glm-5.3-flash")}
                        className="accent-slate-600 w-4 h-4"
                      />
                      GLM-5.3-Flash (多模态架构，高性价比)
                    </label>

                    {/* 2. GLM-4.5-Air 退居第二位 */}
                    <label className="flex items-center gap-2 text-sm cursor-pointer text-stone-700">
                      <input
                        type="radio"
                        name="chatModel"
                        checked={chatModel === "glm-4.5-air"}
                        onChange={() => setChatModel("glm-4.5-air")}
                        className="accent-slate-600 w-4 h-4"
                      />
                      GLM-4.5-Air (更聪明，按量计费)
                    </label>

                    {/* 3. GLM-4.7-Flash 保持最后 */}
                    <label className="flex items-center gap-2 text-sm cursor-pointer text-stone-700">
                      <input
                        type="radio"
                        name="chatModel"
                        checked={chatModel === "glm-4.7-flash"}
                        onChange={() => setChatModel("glm-4.7-flash")}
                        className="accent-slate-600 w-4 h-4"
                      />
                      GLM-4.7-Flash (轻量级，完全免费)
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-2xl border border-yellow-100">
                <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-1">
                  <User className="w-4 h-4" /> 基础信息设定
                </h3>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="text-xs text-yellow-700 block mb-1">
                      用户昵称 (App名字及AI称呼)
                    </label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e: any) => setUserName(e.target.value)}
                      placeholder="例如：安安"
                      className="w-full px-3 py-2 text-sm bg-white border border-yellow-200 rounded-xl outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-yellow-700 block mb-2">
                      知识解答模式 (AI如何回答问题)
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm cursor-pointer text-stone-700">
                        <input
                          type="radio"
                          name="ageMode"
                          checked={ageMode === "child"}
                          onChange={() => setAgeMode("child")}
                          className="accent-yellow-500 w-4 h-4"
                        />
                        儿童模式 (童话比喻)
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer text-stone-700">
                        <input
                          type="radio"
                          name="ageMode"
                          checked={ageMode === "youth"}
                          onChange={() => setAgeMode("youth")}
                          className="accent-yellow-500 w-4 h-4"
                        />
                        青年模式 (专业解答)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-2xl border border-purple-100">
                <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-1">
                  <Bot className="w-4 h-4" /> AI 精灵设定
                </h3>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-purple-700 block mb-1">
                      AI 昵称
                    </label>
                    <input
                      type="text"
                      value={aiName}
                      onChange={(e: any) => setAiName(e.target.value)}
                      placeholder="例如：哈基米"
                      className="w-full px-3 py-2 text-sm bg-white border border-purple-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-purple-700 block mb-1">
                      AI 物种
                    </label>
                    <input
                      type="text"
                      value={aiSpecies}
                      onChange={(e: any) => setAiSpecies(e.target.value)}
                      placeholder="例如：小猫、小狗"
                      className="w-full px-3 py-2 text-sm bg-white border border-purple-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-400"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-2xl border border-green-100">
                <h3 className="font-bold text-green-800 mb-2 flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" /> 语音回复概率
                </h3>
                <p className="text-xs text-green-600 mb-3">
                  调节{aiName}在聊天中发语音消息的频率。当前：{voiceRatio}%
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-stone-500">纯文字</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={voiceRatio}
                    onChange={(e: any) =>
                      setVoiceRatio(parseInt(e.target.value))
                    }
                    className="flex-1 accent-green-500"
                  />
                  <span className="text-xs text-stone-500">全语音</span>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-1">
                  <Wand2 className="w-4 h-4" /> AI 灵感任务池
                </h3>
                <p className="text-xs text-blue-600 mb-3">
                  不知道给{userName}安排什么挑战？告诉 AI 主题，帮你一键生成！
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiInspirationTopic}
                    onChange={(e: any) => setAiInspirationTopic(e.target.value)}
                    placeholder="输入主题，如：自然探索、简单的家务..."
                    className="flex-1 px-3 py-2 text-sm bg-white border border-blue-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    onClick={generateTaskInspiration}
                    disabled={isGeneratingTasks || !aiInspirationTopic.trim()}
                    className="bg-blue-500 text-white px-3 py-2 rounded-xl text-sm font-bold hover:bg-blue-600 transition disabled:opacity-50 whitespace-nowrap"
                  >
                    {isGeneratingTasks ? "施法中..." : "生成并添加"}
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-stone-700 mb-3 flex items-center gap-1">
                  <Edit3 className="w-4 h-4" /> 现有任务列表
                </h3>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTaskInput}
                    onChange={(e: any) => setNewTaskInput(e.target.value)}
                    placeholder="手动输入新任务..."
                    className="flex-1 px-3 py-2 text-sm bg-stone-100 border-none rounded-xl focus:ring-2 focus:ring-pink-400 outline-none"
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter" && newTaskInput.trim()) {
                        setTasks([...tasks, newTaskInput.trim()]);
                        setNewTaskInput("");
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newTaskInput.trim()) {
                        setTasks([...tasks, newTaskInput.trim()]);
                        setNewTaskInput("");
                      }
                    }}
                    className="bg-stone-800 text-white px-4 py-2 rounded-xl hover:bg-stone-700 transition flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {tasks.map((task: any, idx: any) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-2 bg-stone-50 rounded-lg group hover:bg-stone-100 text-sm"
                    >
                      <span className="text-stone-700 truncate mr-2">
                        {task}
                      </span>
                      <button
                        onClick={() =>
                          setTasks(tasks.filter((_: any, i: any) => i !== idx))
                        }
                        className="text-red-400 opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:text-red-600 transition p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 悬浮的动画果冻气泡精灵 */}
      {!showChat && (
        <div
          onPointerDown={onBubblePointerDown}
          onPointerMove={onBubblePointerMove}
          onPointerUp={onBubblePointerUp}
          onPointerCancel={onBubblePointerUp}
          style={{
            transform: `translate(${bubblePos.x}px, ${bubblePos.y}px)`,
            touchAction: "none",
          }}
          className={`fixed bottom-6 right-6 z-40 ${
            isDraggingBubble ? "" : "transition-transform duration-200"
          }`}
        >
          <div
            className={`${
              isDraggingBubble
                ? "cursor-grabbing scale-105"
                : "cursor-grab hover:scale-110"
            } transition-transform`}
          >
            <div
              style={{
                animation: isDraggingBubble
                  ? "none"
                  : "jelly-bounce 4s infinite",
              }}
            >
              <div
                className="relative w-16 h-16 shadow-[0_0_20px_rgba(110,255,180,0.6)] border border-white/60"
                style={{
                  background:
                    "radial-gradient(circle at 35% 35%, #cafff4 0%, #8af5c5 60%, #5ce5a3 100%)",
                  animation: "blob-shape 4s infinite ease-in-out",
                }}
              >
                <div className="absolute top-[42%] left-0 w-full flex justify-center gap-2">
                  <div
                    className="w-1.5 h-3 bg-stone-800 rounded-full rotate-[-5deg]"
                    style={{ animation: "eye-blink 4s infinite" }}
                  ></div>
                  <div
                    className="w-1.5 h-3 bg-stone-800 rounded-full rotate-[5deg]"
                    style={{ animation: "eye-blink 4s infinite" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 恢复固定的遮罩聊天弹窗 */}
      {showChat && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50">
          <div className="bg-stone-50 md:rounded-3xl w-full h-[85vh] md:h-[600px] max-w-md shadow-2xl flex flex-col overflow-hidden rounded-t-3xl md:rounded-b-3xl">
            <div className="bg-pink-500 p-4 text-white flex justify-between items-center shrink-0">
              <span className="font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> 魔法{aiName}聊天室
              </span>
              <button
                onClick={() => setShowChat(false)}
                className="hover:text-pink-200"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {messages.map((msg: any, idx: any) => (
                <div
                  key={idx}
                  className={`flex ${
                    msg.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {msg.role === "user" ? (
                    <div className="max-w-[75%] p-3 rounded-2xl bg-pink-400 text-white rounded-tr-sm shadow-sm">
                      {msg.text}
                    </div>
                  ) : msg.type === "loading_audio" ? (
                    <div className="bg-white text-stone-400 shadow-sm rounded-tl-sm border border-stone-100 p-3 rounded-2xl text-sm flex items-center gap-2">
                      <span className="animate-pulse">🎤</span> {aiName}
                      正在录音...
                    </div>
                  ) : msg.type === "audio" ? (
                    <div className="flex flex-col gap-1 items-start max-w-[75%]">
                      <div
                        onClick={() => playAudio(msg.audioUrl, idx)}
                        className="bg-white text-stone-700 shadow-sm rounded-tl-sm border border-stone-100 p-3 rounded-2xl flex items-center gap-3 cursor-pointer w-40 hover:bg-stone-50 active:bg-stone-100 transition"
                      >
                        <span className="text-green-500">
                          {playingMsgIndex === idx ? "🔊" : "🔈"}
                        </span>
                        <span className="text-sm font-medium text-stone-500">
                          {playingMsgIndex === idx ? "播放中..." : "语音消息"}
                        </span>
                      </div>
                      <span className="text-xs text-stone-400 pl-1 whitespace-pre-wrap">
                        {msg.text}
                      </span>
                    </div>
                  ) : (
                    <div className="max-w-[75%] p-3 rounded-2xl bg-white text-stone-700 shadow-sm rounded-tl-sm border border-stone-100 whitespace-pre-wrap">
                      {msg.text}
                    </div>
                  )}
                </div>
              ))}
              {isChatting && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-tl-sm shadow-sm text-stone-400 text-sm">
                    {aiName}正在敲键盘...
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white p-3 border-t border-stone-200 flex items-center gap-2 shrink-0">
              <input
                type="text"
                value={inputText}
                onChange={(e: any) => setInputText(e.target.value)}
                onKeyDown={(e: any) => e.key === "Enter" && handleSendMessage()}
                placeholder={`跟${aiName}说点什么...`}
                className="flex-1 bg-stone-100 px-4 py-2 rounded-full outline-none focus:ring-2 focus:ring-pink-300"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isChatting}
                className="bg-pink-500 text-white px-4 py-2 rounded-full font-bold hover:bg-pink-600 disabled:opacity-50 transition-colors"
              >
                发送
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
